import type { Company } from "@domain/CRM/company";
import type { CompanySettings } from "@domain/Settings";
import type { LogData } from "@rogoag/shared-ts";
import type { ICompanies } from "@ports/companies/ICompanies";
import type { ILogger } from "@ports/logger/ILogger";

export interface ImportSettingsUseCasesDeps {
	companiesApi: ICompanies;
	logger: ILogger;
}

/** Visibility/required pair for a single import-table column — the shape
 *  the use case persists into `Company.portalSettings.importTableColumns`. */
export interface ImportColumnSetting {
	visible: boolean;
	required: boolean;
}

export interface SaveImportSettingsInput {
	company: Company;
	columnSettings: Record<string, ImportColumnSetting>;
	importFlowSettings: Record<string, boolean>;
	/** The set of `portalConfigurationOptions` keys that this UI manages.
	 *  Any key in `company.portalConfigurationOptions` that is NOT in this set
	 *  is *preserved* during the merge — that's how the page avoids clobbering
	 *  unrelated flags (e.g. `Submission: ...` options) it doesn't render. */
	importFlowOptionKeys: readonly string[];
}

/**
 * Drives the Admin → ImportSettings page.
 *
 *  - `loadCompanies()` — fetches every active company; the page renders a
 *    selector populated from this list.
 *
 *  - `save(input)` — orchestrates two backend updates plus a critical merge:
 *      1. `updatePortalSettings(company.id, { ...existing, importTableColumns })`
 *         — persists the column visibility/required map.
 *      2. `updatePortalConfigurationOptions(company.id, mergedOptions)`
 *         — when `importFlowSettings` differs from the existing flags. The
 *         merged list is `[enabled-from-flow, ...preserved-non-import]`:
 *         enabled keys come from `importFlowSettings`; everything in the
 *         current `portalConfigurationOptions` that is NOT in
 *         `importFlowOptionKeys` is appended unchanged so unrelated flags
 *         survive.
 *      Returns the updated Company object the page should put in state.
 *
 * The merge invariant — "this UI only owns the keys it actually renders" —
 * is what the bd ticket flagged as untested before extraction. The unit
 * tests below pin every merge case (no flow change, partial enable,
 * non-import preservation, full disable).
 */
export interface ImportSettingsUseCases {
	loadCompanies(): Promise<Company[]>;
	save(input: SaveImportSettingsInput): Promise<Company>;
}

export function loadCompanies(deps: ImportSettingsUseCasesDeps): Promise<Company[]> {
	return deps.companiesApi.getByFilter({
		type: "OR",
		fields: [{ isActive: 1 }],
	} as never);
}

export async function save(deps: ImportSettingsUseCasesDeps, input: SaveImportSettingsInput): Promise<Company> {
	const logData: LogData = {
		function: "importSettings.save",
		airtable_record_id: input.company.id,
		airtable_table: "Companies",
	};
	deps.logger.log("Saving import settings", logData);

	const updatedSettings = {
		...(input.company.portalSettings || { views: [] }),
		importTableColumns: input.columnSettings as CompanySettings["importTableColumns"],
	} as CompanySettings;

	await deps.companiesApi.updatePortalSettings(input.company.id, updatedSettings);

	const mergedOptions = mergePortalConfigurationOptions(
		input.company.portalConfigurationOptions ?? [],
		input.importFlowSettings,
		input.importFlowOptionKeys,
	);

	// Only round-trip the configuration-options endpoint when the merged
	// list actually differs from what the company already has. The vm
	// previously gated on a JSON.stringify equality of the per-key map;
	// the array-equality check here is equivalent for the keys this UI
	// owns and avoids a no-op POST in the common save-with-no-flow-change
	// case.
	const existing = (input.company.portalConfigurationOptions ?? []) as readonly string[];
	const existingSet = new Set(existing);
	const mergedSet = new Set(mergedOptions);
	const optionsChanged = existingSet.size !== mergedSet.size || mergedOptions.some((opt) => !existingSet.has(opt));
	if (optionsChanged) {
		await deps.companiesApi.updatePortalConfigurationOptions(input.company.id, mergedOptions);
	}

	deps.logger.log("Import settings saved successfully", logData);
	return {
		...input.company,
		portalSettings: updatedSettings,
		portalConfigurationOptions: mergedOptions as Company["portalConfigurationOptions"],
	};
}

export function makeImportSettingsUseCases(deps: ImportSettingsUseCasesDeps): ImportSettingsUseCases {
	return {
		loadCompanies: () => loadCompanies(deps),
		save: (input) => save(deps, input),
	};
}

/**
 * Pure merge: keys in `importFlowSettings` that are `true` come first
 * (in insertion order), followed by every entry in `existing` whose key
 * is NOT in `importFlowOptionKeys`. The non-import keys preserve their
 * relative order from the original list.
 */
export function mergePortalConfigurationOptions(
	existing: readonly string[],
	importFlowSettings: Record<string, boolean>,
	importFlowOptionKeys: readonly string[],
): string[] {
	const enabled = Object.entries(importFlowSettings)
		.filter(([, v]) => v)
		.map(([k]) => k);
	const importKeys = new Set(importFlowOptionKeys);
	const preserved = existing.filter((opt) => !importKeys.has(opt));
	return [...enabled, ...preserved];
}
